

import React, { useCallback, useEffect, useState } from "react";
import { ChannelProps, ChatMessage, Conversation } from "../../../../../views/crm-pages/conversation/types";
import { InsightsPanel } from "../../components/insight";
import {
  EmailSidebar,
  EmailTimelinePanel,
  EmailDetailView,
  EmailReplyPanel,
  EmailConversation,
  EmailMessage,
  EmailComposeData,
} from "./components";
import { toast } from "sonner";
import { Icons } from "../../../../../views/crm-pages/conversation/constant";
import useCrmApi from "../../../../hook/crm-req";

/**
 * View mode determines which panels are shown
 * - list: Conversation list + timeline (Steps 1 & 2)
 * - timeline: User sidebar + email detail (Step 3)
 * - compose: User sidebar + reply composer (Step 4)
 */
type EmailViewMode = "list" | "timeline" | "compose";

const SUPPORT_EMAIL = "support@convoai.com";

const getResponseArray = (response: any, keys: string[]): any[] => {
  if (Array.isArray(response)) return response;
  for (const key of keys) {
    if (Array.isArray(response?.[key])) return response[key];
    if (Array.isArray(response?.data?.[key])) return response.data[key];
  }
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.results)) return response.results;
  return [];
};

const getEmailContact = (value?: string | null, name?: string | null): { id: string; name: string; email: string } => {
  const email = value && value.includes("@") ? value : "";
  const displayName = name || email || "Unknown";
  return {
    id: email || displayName,
    name: displayName,
    email: email || `${displayName.toLowerCase().replace(/\s+/g, ".")}@unknown.local`,
  };
};

const getMessageBody = (value: any): string => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return String(value.body || value.message || value.text || value.content || "");
  }
  return String(value);
};

const getConversationLastMessageBody = (conversation: Conversation): string => {
  const lastMessage = conversation.last_message as any;
  if (!lastMessage) return "";
  return getMessageBody(lastMessage);
};

const toEmailMessage = (message: Partial<ChatMessage> & Record<string, any>, conversation: Conversation): EmailMessage => {
  const isOutbound = message.direction === "outbound";
  const customer = getEmailContact(
    conversation.external_thread_id || message.recipient || message.from_email || message.to_email,
    conversation.title,
  );
  const support = getEmailContact(SUPPORT_EMAIL, "Support");
  const body = getMessageBody(message.body || message.message || message.text || message.content);

  return {
    id: String(message.id || message.external_message_id || `${conversation.id}-${message.created_at || Date.now()}`),
    conversationId: conversation.id,
    subject: message.subject || conversation.title || "Email Conversation",
    from: isOutbound ? support : customer,
    to: [isOutbound ? customer : support],
    body,
    plainText: body.replace(/<[^>]*>/g, ""),
    attachments: [],
    direction: isOutbound ? "outbound" : "inbound",
    status: (message.status as EmailMessage["status"]) || "sent",
    sentAt: message.sent_at || message.created_at || conversation.last_message_at || conversation.created_at || new Date().toISOString(),
    readAt: message.read_at || undefined,
    isRead: Boolean(message.read_at) || isOutbound,
  };
};

const toEmailConversation = (conversation: Conversation, messages: EmailMessage[] = []): EmailConversation => {
  const lastMessageBody = getConversationLastMessageBody(conversation);
  const rawLastMessage = conversation.last_message as any;
  const fallbackMessage = toEmailMessage(
    {
      id: rawLastMessage?.id || `last-${conversation.id}`,
      conversation_id: conversation.id,
      body: lastMessageBody,
      direction: rawLastMessage?.direction || "inbound",
      created_at: rawLastMessage?.created_at || conversation.last_message_at || conversation.created_at,
      sent_at: rawLastMessage?.sent_at,
      status: rawLastMessage?.status || "sent",
      subject: rawLastMessage?.subject,
      recipient: rawLastMessage?.recipient,
    },
    conversation,
  );
  const emailMessages = messages.length > 0 ? messages : lastMessageBody ? [fallbackMessage] : [];
  const customer = getEmailContact(conversation.external_thread_id, conversation.title);
  const support = getEmailContact(SUPPORT_EMAIL, "Support");

  return {
    id: conversation.id,
    subject: conversation.title || emailMessages[0]?.subject || "Email Conversation",
    participants: [customer, support],
    lastMessage: emailMessages[emailMessages.length - 1] || fallbackMessage,
    messages: emailMessages,
    unreadCount: conversation.unread_count || 0,
    status: conversation.status || "open",
    createdAt: conversation.created_at || new Date().toISOString(),
    updatedAt: conversation.last_message_at || conversation.created_at || new Date().toISOString(),
    labels: [],
    isStarred: false,
  };
};

/**
 * Email Channel Component
 */
export const EmailChannel: React.FC<ChannelProps> = ({
  leadContact,
  assignedTicket,
  team,
  isMobile,
  mobileView,
  setMobileView,
  showInsights,
  setShowInsights,
  activeInsightTab,
  setActiveInsightTab,
}) => {
  // ==========================================
  // STATE
  // ==========================================

  const [viewMode, setViewMode] = useState<EmailViewMode>("list");
  const [emailConversations, setEmailConversations] = useState<EmailConversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<EmailConversation | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTab, setFilterTab] = useState(0);
  const token = localStorage.getItem("authToken");
  const { makeRequest: getEmailConversations } = useCrmApi("/conversations", "GET", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: getConversationMessages } = useCrmApi(
    selectedConversation?.id ? `/conversations/${selectedConversation.id}/messages` : "/conversations/messages",
    "GET",
    { Authorization: `Bearer ${token}` },
  );
  const { makeRequest: sendEmailMessage } = useCrmApi("/messages/send", "POST", {
    Authorization: `Bearer ${token}`,
  });

  // ==========================================
  // DATA LOADING
  // ==========================================

  const loadConversations = useCallback(async () => {
    setLoading(true);
    try {
      const [response, statusCode] = await getEmailConversations(undefined, { channel: "email" });
      if (statusCode >= 400 || response?.error || response?.detail) {
        throw new Error(response?.detail || response?.error || "Failed to load email conversations");
      }

      const conversations = getResponseArray(response, ["conversations"]) as Conversation[];
      setEmailConversations(conversations.map((conversation) => toEmailConversation(conversation)));
    } catch (error) {
      console.error("Failed to load email conversations:", error);
      toast.error("Failed to load emails");
      setEmailConversations([]);
    } finally {
      setLoading(false);
    }
  }, [getEmailConversations]);

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    const loadMessages = async () => {
      if (!selectedConversation) return;
      try {
        const [response, statusCode] = await getConversationMessages();
        if (statusCode >= 400 || response?.error || response?.detail) {
          throw new Error(response?.detail || response?.error || "Failed to load email messages");
        }

        const rawMessages = getResponseArray(response, ["messages"]) as Array<Partial<ChatMessage> & Record<string, any>>;
        const originalConversation = {
          id: selectedConversation.id,
          title: selectedConversation.subject,
          external_thread_id: selectedConversation.participants.find((p) => p.email !== SUPPORT_EMAIL)?.email || "",
          status: selectedConversation.status,
          last_message_at: selectedConversation.updatedAt,
          created_at: selectedConversation.createdAt,
        } as Conversation;
        const messages = rawMessages
          .map((message) => toEmailMessage(message, originalConversation))
          .sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime());
        const updated = toEmailConversation(originalConversation, messages);
        setSelectedConversation(updated);
        setEmailConversations((prev) => prev.map((item) => item.id === updated.id ? updated : item));
      } catch (error) {
        console.error("Failed to load email messages:", error);
      }
    };

    loadMessages();
  }, [selectedConversation?.id]);

  // ==========================================
  // NAVIGATION HANDLERS
  // ==========================================

  /**
   * Step 1 → Step 2: Select a conversation
   * Automatically select the first email to show in detail view
   */
  const handleSelectConversation = (conversation: EmailConversation) => {
    setSelectedConversation(conversation);
    // Don't auto-select email - show timeline in center first
    setSelectedEmail(null);
    setViewMode("list");

    // TODO: Call API to mark as read
    if (conversation.unreadCount > 0) {
      setEmailConversations((prev) =>
        prev.map((c) =>
          c.id === conversation.id ? { ...c, unreadCount: 0 } : c
        )
      );
    }

    if (isMobile) setMobileView("chat");
  };

  /**
   * Step 2 → Step 3: Select an email to view details
   */
  const handleSelectEmail = (email: EmailMessage) => {
    setSelectedEmail(email);
    setViewMode("timeline");
  };

  /**
   * Step 3 → Step 4: Click reply button
   */
  const handleReply = (email?: EmailMessage) => {
    const emailToReply = email || selectedEmail || selectedConversation?.messages.slice(-1)[0];
    if (emailToReply) {
      setSelectedEmail(emailToReply);
      setViewMode("compose");
    }
  };

  /**
   * Step 4 → Step 3: Go back from compose to detail view
   */
  const handleBackFromCompose = () => {
    setViewMode("timeline");
  };

  /**
   * Step 3 → Step 2: Close email detail, go back to timeline
   */
  const handleCloseDetail = () => {
    setSelectedEmail(null);
    setViewMode("list");
  };

  /**
   * Reset to Step 1: Clear all selections
   */
  const handleBackToList = () => {
    setSelectedConversation(null);
    setSelectedEmail(null);
    setViewMode("list");
  };

  // ==========================================
  // EMAIL ACTIONS
  // ==========================================

  const handleSendEmail = async (data: EmailComposeData) => {
    if (!selectedConversation) return;
    try {
      await sendEmailMessage({
        conversation_id: selectedConversation.id,
        recipient: data.to[0],
        body: data.body,
        subject: data.subject,
        type: "email",
        channel: "email",
      });
      toast.success("Email sent successfully!");
      handleBackFromCompose();
      loadConversations();
    } catch (error) {
      console.error("Failed to send email:", error);
      toast.error("Failed to send email");
    }
  };

  /**
   * Handle saving draft
   * TODO: Integrate with POST /api/email/drafts
   */
  const handleSaveDraft = async (data: EmailComposeData) => {
    try {

      toast.success("Draft saved!");
    } catch (error) {
      console.error("Failed to save draft:", error);
      toast.error("Failed to save draft");
    }
  };

  /**
   * Handle compose new email (placeholder)
   */
  const handleComposeNew = () => {
    // TODO: Implement new email composition
    toast.info("New email composition coming soon!");
  };

  // ==========================================
  // RENDER HELPERS
  // ==========================================

  const renderLeftPanel = () => {
    // Step 3 & 4: Show user detail + email timeline when viewing email detail or composing
    if ((viewMode === "timeline" || viewMode === "compose") && selectedConversation && selectedEmail) {
      return (
        <EmailTimelinePanel
          conversation={selectedConversation}
          onSelectEmail={handleSelectEmail}
          onReply={handleReply}
          onOpenInsights={() => setShowInsights(true)}
          isMobile={isMobile}
          selectedEmailId={selectedEmail?.id}
          onBack={handleBackToList}
        />
      );
    }

    // Steps 1 & 2: Show conversation list
    return (
      <EmailSidebar
        conversations={emailConversations}
        selectedConversation={selectedConversation}
        onSelectConversation={handleSelectConversation}
        onComposeNew={handleComposeNew}
        loading={loading}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        activeTab={filterTab}
        onTabChange={setFilterTab}
      />
    );
  };

  const renderCenterPanel = () => {
    // Step 4: Reply composer
    if (viewMode === "compose" && selectedConversation && selectedEmail) {
      return (
        <EmailReplyPanel
          conversation={selectedConversation}
          selectedEmail={selectedEmail}
          onSend={handleSendEmail}
          onSaveDraft={handleSaveDraft}
          onBack={handleBackFromCompose}
        />
      );
    }

    // Step 3: Email detail view
    if (viewMode === "timeline" && selectedConversation && selectedEmail) {
      return (
        <EmailDetailView
          email={selectedEmail}
          onReply={() => handleReply()}
          onForward={() => handleReply(selectedEmail)}
          onClose={handleCloseDetail}
          onOpenInsights={() => setShowInsights(true)}
          isMobile={isMobile}
        />
      );
    }

    // Step 2: Email timeline (conversation selected, no email selected yet)
    if (viewMode === "list" && selectedConversation) {
      return (
        <EmailTimelinePanel
          conversation={selectedConversation}
          onSelectEmail={handleSelectEmail}
          onReply={handleReply}
          onOpenInsights={() => setShowInsights(true)}
          isMobile={isMobile}
          selectedEmailId={selectedEmail?.id}
          onBack={handleBackToList}
          fullWidth={true}
        />
      );
    }

    // Step 1: Empty state (no conversation selected)
    return (
      <div className="flex-1 bg-[#f7f8fb] dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center px-6">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 text-indigo-500 dark:text-indigo-400 shadow-sm">
            <Icons.Email size={28} />
          </div>
          <h2 className="text-lg font-semibold text-slate-700 dark:text-gray-200">
            Select a conversation
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">
            Choose an email conversation from the list to view messages
          </p>
        </div>
      </div>
    );
  };

  const handleMobileBack = () => {
    if (viewMode === "compose") {
      handleBackFromCompose();
    } else if (viewMode === "timeline") {
      handleCloseDetail();
    } else if (selectedConversation) {
      handleBackToList();
    } else {
      setMobileView("list");
    }
  };

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <>
      {/* Mobile Back Button */}
      {isMobile && mobileView === "chat" && (
        <div className="fixed top-[130px] left-2 z-[100]">
          <button
            onClick={handleMobileBack}
            className="bg-indigo-500 text-white border-none rounded-lg px-3 py-1.5 text-xs font-bold cursor-pointer"
          >
            ← Back
          </button>
        </div>
      )}

      {/* Left Panel */}
      {(!isMobile || mobileView === "list") && renderLeftPanel()}

      {/* Center Panel */}
      {(!isMobile || mobileView === "chat") && renderCenterPanel()}

      {/* Right Panel - Insights (Desktop only) */}
      {!isMobile && (
        <InsightsPanel
          activeInsightTab={activeInsightTab}
          onTabChange={setActiveInsightTab}
          leadContact={leadContact}
          assignedTicket={assignedTicket}
          loadingLead={false}
          team={team}
        />
      )}

      {/* Mobile Insights Drawer */}
      {isMobile && showInsights && (
        <div
          className="fixed inset-0 bg-black/35 z-200 flex justify-end"
          onClick={() => setShowInsights(false)}
        >
          <div
            className="w-[85%] max-w-[320px] bg-white dark:bg-gray-900 h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-slate-100 dark:border-gray-800 flex justify-between">
              <span className="font-extrabold dark:text-gray-100">ConvoAI Insights</span>
              <button
                onClick={() => setShowInsights(false)}
                className="border-none bg-transparent cursor-pointer text-lg dark:text-gray-300"
              >
                ×
              </button>
            </div>
            <InsightsPanel
              activeInsightTab={activeInsightTab}
              onTabChange={setActiveInsightTab}
              leadContact={leadContact}
              assignedTicket={assignedTicket}
              loadingLead={false}
              team={team}
            />
          </div>
        </div>
      )}
    </>
  );
};
